import React, { useEffect, useMemo, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import LoadingSpinner from './LoadingSpinner';

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const popIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(10px) scale(0.985);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
`;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 10020;
  display: flex;
  align-items: center;
  justify-content: center;
  padding:
    max(0.7rem, env(safe-area-inset-top, 0px))
    max(0.7rem, env(safe-area-inset-right, 0px))
    max(0.7rem, env(safe-area-inset-bottom, 0px))
    max(0.7rem, env(safe-area-inset-left, 0px));
  background: radial-gradient(circle at top, rgba(31, 58, 86, 0.5), rgba(8, 16, 28, 0.92));
  backdrop-filter: blur(6px);
  animation: ${fadeIn} 0.22s ease forwards;
`;

const Panel = styled.div`
  width: min(96vw, 980px);
  height: min(92dvh, 780px);
  max-height: min(92dvh, 780px);
  border-radius: 18px;
  border: 1px solid rgba(208, 222, 236, 0.36);
  background: linear-gradient(180deg, #f9fcff 0%, #edf5fd 100%);
  box-shadow: 0 24px 52px rgba(0, 0, 0, 0.5);
  padding: 0.6rem;
  display: flex;
  flex-direction: column;
  gap: 0.52rem;
  animation: ${popIn} 0.2s ease forwards;

  @media (max-width: 1024px) {
    width: min(98vw, 98vw);
    height: 92dvh;
    max-height: 92dvh;
    border-radius: 14px;
    padding: 0.42rem;
  }
`;

const Header = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.6rem;
  padding: 0.2rem 0.2rem 0;
`;

const TitleWrap = styled.div`
  min-width: 0;
`;

const Title = styled.div`
  margin: 0;
  color: #1a2e40;
  font-size: 0.96rem;
  font-weight: 800;
  line-height: 1.28;
  word-break: break-word;
`;

const Meta = styled.div`
  margin-top: 0.15rem;
  color: #5f7286;
  font-size: 0.76rem;
  font-weight: 600;
  line-height: 1.3;
  word-break: break-word;
`;

const CloseButton = styled.button`
  width: 36px;
  height: 36px;
  min-width: 36px;
  border: 1px solid rgba(255, 255, 255, 0.38);
  border-radius: 999px;
  background: rgba(15, 23, 42, 0.86);
  color: #ffffff;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.35);
  transition: background 0.2s ease, transform 0.25s cubic-bezier(0.4, 0, 0.2, 1);

  svg {
    width: 16px;
    height: 16px;
    display: block;
  }

  &:hover {
    background: rgba(15, 23, 42, 1);
    transform: scale(1.08) rotate(90deg);
  }

  &:active {
    transform: scale(0.94) rotate(90deg);
  }

  &:focus-visible {
    outline: 2px solid #7fb3e0;
    outline-offset: 2px;
  }
`;

const Media = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 14px;
  background: #ffffff;
  border: 1px solid #d9e3ef;
  overflow: hidden;
  padding: 0.28rem;
`;

const StyledImage = styled.img`
  width: 100%;
  height: 100%;
  max-width: 100%;
  max-height: 100%;
  border-radius: 11px;
  box-shadow: 0 14px 30px rgba(0, 0, 0, 0.22);
  background: #fff;
  object-fit: contain;
`;

const Gallery = styled.div`
  display: flex;
  gap: 0.5rem;
  overflow-x: auto;
  padding: 0.45rem 0.1rem 0.1rem;

  &::-webkit-scrollbar {
    height: 6px;
  }
`;

const Thumbnail = styled.button`
  width: 72px;
  height: 72px;
  flex-shrink: 0;
  border-radius: 10px;
  border: ${({ $active }) => ($active ? '2px solid #1f6fb2' : '1px solid #d9e3ef')};
  overflow: hidden;
  background: white;
  padding: 0;
  cursor: pointer;
  transition: transform 0.15s ease;

  &:hover {
    transform: scale(1.04);
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const Navigation = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 0.4rem;
`;

const NavButton = styled.button`
  border: none;
  background: #1a2e40;
  color: white;
  border-radius: 999px;
  width: 38px;
  height: 38px;
  cursor: pointer;
  font-size: 1rem;

  &:disabled {
    opacity: 0.35;
    cursor: not-allowed;
  }
`;

// Groupes de champs OpenFoodFacts : pour chaque type de photo, on garde
// la première variante d'URL disponible (pleine taille > small > thumb).
const FOOD_IMAGE_GROUPS = [
  ['image_front_url', 'image_front_small_url', 'image_front_thumb_url', 'image_url', 'image_small_url', 'image_thumb_url', 'src'],
  ['image_ingredients_url', 'image_ingredients_small_url', 'image_ingredients_thumb_url'],
  ['image_nutrition_url', 'image_nutrition_small_url', 'image_nutrition_thumb_url'],
  ['image_packaging_url', 'image_packaging_small_url', 'image_packaging_thumb_url'],
];

export const getFoodImageGallery = (imageData = {}) => {
  const imageUrls = FOOD_IMAGE_GROUPS
    .map((fields) => fields
      .map((field) => String(imageData?.[field] || '').trim())
      .find(Boolean)
    )
    .filter(Boolean);

  return [...new Set(imageUrls)];
};

const CloseIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round">
    <line x1="5" y1="5" x2="19" y2="19" />
    <line x1="19" y1="5" x2="5" y2="19" />
  </svg>
);

const FoodImageModal = ({
  isOpen = false,
  imageSrc = '',
  images = [],
  fallbackSrc = '',
  title = 'Aperçu aliment',
  subtitle = '',
  code = '',
  onClose,
}) => {
  const availableImages = useMemo(() => {
    const list = [];

    const addImage = (image) => {
      const imageUrl = String(image || '').trim();

      if (imageUrl && !list.includes(imageUrl)) {
        list.push(imageUrl);
      }
    };

    addImage(imageSrc);
    images.forEach(addImage);

    // Le placeholder ne doit servir que s'il n'y a vraiment aucune image
    if (list.length === 0 && fallbackSrc) {
      addImage(fallbackSrc);
    }

    return list;
  }, [imageSrc, images, fallbackSrc]);

  const availableImagesKey = availableImages.join('\u0001');

  const [currentIndex, setCurrentIndex] = useState(0);
  const [loadedImages, setLoadedImages] = useState(() => new Set());

  useEffect(() => {
    if (isOpen) {
      setCurrentIndex(0);
      setLoadedImages(new Set());
    }
  }, [isOpen, availableImagesKey]);

  useEffect(() => {
    if (!isOpen || availableImages.length === 0) {
      return undefined;
    }

    let cancelled = false;
    const preloaders = availableImages.map((src) => {
      const img = new Image();
      img.onload = () => {
        if (cancelled) return;
        setLoadedImages((prev) => (prev.has(src) ? prev : new Set(prev).add(src)));
      };
      img.onerror = () => {
        if (cancelled) return;
        setLoadedImages((prev) => (prev.has(src) ? prev : new Set(prev).add(src)));
      };
      img.src = src;
      return img;
    });

    return () => {
      cancelled = true;
      preloaders.forEach((img) => {
        img.onload = null;
        img.onerror = null;
      });
    };
  }, [isOpen, availableImagesKey]);

  const allImagesLoaded = availableImages.length > 0 && loadedImages.size >= availableImages.length;
  const currentSrc = availableImages[currentIndex] || '';

  const handleClose = () => {
    if (typeof onClose === 'function') {
      onClose();
    }
  };

  useEffect(() => {
    if (!isOpen || typeof document === 'undefined') {
      return undefined;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const handleKeydown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        handleClose();
        return;
      }

      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        setCurrentIndex((i) => Math.max(0, i - 1));
        return;
      }

      if (event.key === 'ArrowRight') {
        event.preventDefault();
        setCurrentIndex((i) => Math.min(availableImages.length - 1, i + 1));
      }
    };

    document.addEventListener('keydown', handleKeydown);
    return () => {
      document.removeEventListener('keydown', handleKeydown);
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen, onClose, availableImages.length]);

  const metaLine = useMemo(
    () => [subtitle, code ? `Code: ${code}` : null].filter(Boolean).join(' • '),
    [subtitle, code]
  );

  if (!isOpen || !currentSrc) {
    return null;
  }

  const handleOverlayClick = (event) => {
    if (event.target === event.currentTarget) {
      handleClose();
    }
  };

  return (
    <Overlay
      role="dialog"
      aria-modal="true"
      aria-label={`Image agrandie: ${title || 'aliment'}`}
      onClick={handleOverlayClick}
    >
      <Panel onClick={(event) => event.stopPropagation()}>
        <Header>
          <TitleWrap>
            <Title title={title}>{title || 'Aperçu aliment'}</Title>
            {metaLine ? <Meta>{metaLine}</Meta> : null}
          </TitleWrap>
          <CloseButton type="button" onClick={handleClose} aria-label="Fermer l'image agrandie" title="Fermer">
            <CloseIcon />
          </CloseButton>
        </Header>

        <Media>
          {allImagesLoaded ? (
            <StyledImage src={currentSrc} alt={title || 'Image aliment'} />
          ) : (
            <LoadingSpinner type="bars" size="medium" color="primary" text="Chargement des images..." showText />
          )}
        </Media>

        {allImagesLoaded && availableImages.length > 1 && (
          <>
            <Navigation>
              <NavButton
                type="button"
                aria-label="Image précédente"
                disabled={currentIndex === 0}
                onClick={() => setCurrentIndex((i) => i - 1)}
              >
                ←
              </NavButton>

              <div style={{ fontWeight: 600, color: '#5f7387' }}>
                {currentIndex + 1} / {availableImages.length}
              </div>

              <NavButton
                type="button"
                aria-label="Image suivante"
                disabled={currentIndex === availableImages.length - 1}
                onClick={() => setCurrentIndex((i) => i + 1)}
              >
                →
              </NavButton>
            </Navigation>

            <Gallery>
              {availableImages.map((src, index) => (
                <Thumbnail
                  key={src}
                  $active={index === currentIndex}
                  onClick={() => setCurrentIndex(index)}
                  aria-label={`Miniature ${index + 1}`}
                >
                  <img src={src} alt="" />
                </Thumbnail>
              ))}
            </Gallery>
          </>
        )}
      </Panel>
    </Overlay>
  );
};

export default FoodImageModal;